import type { Ref } from 'vue'
import type { BarSeriesOption, LineSeriesOption } from 'echarts/charts'

import { onBeforeUnmount } from 'vue'
import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'
import { CHART_BLUE } from './echarts-colors'
import { EchartsHost } from './echarts-host'

interface DataPoint {
  date: string
  value: number
  formattedDate: string
  formattedValue: string
}

interface ChartUrls {
  ytd: string | null
  ptd: string | null
}

const YTD_BAR_COLOR = CHART_BLUE
const PTD_LINE_COLOR = '#70ad47'
const PTD_LINE_STROKE_COLOR = '#c0c0c0'

export function useElementTrendChart(containerRef: Ref<HTMLElement | null>): {
  renderChart: (chartUrls: ChartUrls) => Promise<void>
  disposeChart: () => void
} {
  let host: EchartsHost | null = null

  async function renderChart(chartUrls: ChartUrls): Promise<void> {
    if (!containerRef.value) return
    if (!chartUrls.ytd) return

    try {
      const [ytdData, ptdData] = await Promise.all([
        SFetch.json<{ dataPoints: DataPoint[] }>(chartUrls.ytd),
        chartUrls.ptd
          ? SFetch.json<{ dataPoints: DataPoint[] }>(chartUrls.ptd)
          : null,
      ])

      const ytdPoints = ytdData?.dataPoints || []
      if (ytdPoints.length === 0) return

      const ptdPoints = ptdData?.dataPoints || []

      if (!containerRef.value) return

      disposeChart()
      const chartHost = new EchartsHost()
      host = chartHost
      // mount() returns null when disposeChart() ran during the chunk load.
      const chart = await chartHost.mount(containerRef.value)
      if (!chart) return

      const categories = ytdPoints.map((p) => p.formattedDate)
      const ytdValues = ytdPoints.map((p) => p.value)
      const ptdValues = ptdPoints.map((p) => p.value)

      const series: (BarSeriesOption | LineSeriesOption)[] = [
        {
          name: 'YTD',
          type: 'bar',
          data: ytdValues,
          itemStyle: { color: YTD_BAR_COLOR },
          barMaxWidth: 60,
        },
      ]

      if (ptdValues.length > 0) {
        series.push({
          name: 'PTD',
          type: 'line',
          data: ptdValues,
          itemStyle: { color: PTD_LINE_COLOR },
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { color: PTD_LINE_STROKE_COLOR },
        })
      }

      chart.setOption({
        tooltip: {
          trigger: 'axis',
        },
        legend: {
          bottom: 0,
          data: series.map((s) => s.name),
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: categories,
        },
        yAxis: {
          type: 'value',
        },
        series,
      })
    } catch (error) {
      SfLog.warn('Failed to render element trend chart', { error })
    }
  }

  function disposeChart(): void {
    host?.dispose()
    host = null
  }

  onBeforeUnmount(() => {
    disposeChart()
  })

  return { renderChart, disposeChart }
}
